import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Award, Globe, TrendingUp, Heart, Building2, Ship, Handshake, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const companyData = {
  tagline: "Bridging Markets, Building Trust",
  description: "Triton Global Trade is a premier merchant exporter based in India, founded in 2025. Led by a visionary entrepreneur with over 8 years of industry experience, we are dedicated to connecting international supermarkets and retailers with the finest Indian products. We specialize in exporting authentic spices, premium FMCG products, traditional pooja articles, quality household items, and much more.",
  mission: "To be the most reliable bridge between Indian manufacturers and global retailers, delivering exceptional products that meet international standards while nurturing long-term partnerships built on trust, quality, and mutual growth.",
  vision: "To become the preferred sourcing partner for supermarkets worldwide, making authentic Indian products accessible across every continent while upholding the highest standards of quality and service excellence.",
  values: [
    { title: "Quality First", description: "Every product we export undergoes rigorous quality checks to ensure it meets international standards." },
    { title: "Reliability", description: "Consistent supply chains and timely deliveries you can count on, every time." },
    { title: "Customer Focus", description: "Your success is our priority. We go the extra mile to understand and meet your unique requirements." },
    { title: "Integrity", description: "Transparent dealings and ethical business practices form the foundation of all our relationships." }
  ],
  whyChooseUs: [
    {
      title: "Extensive Product Range",
      description: "From aromatic spices and premium teas to household essentials and religious items, we offer a comprehensive catalog covering 36+ categories and 1600+ products from 220+ trusted brands."
    },
    {
      title: "Quality Certified Products",
      description: "We source only from certified manufacturers and conduct thorough quality inspections. All our products meet FSSAI, ISO, and international export standards."
    },
    {
      title: "Customized Solutions",
      description: "We offer private labeling, custom packaging, and assorted container loading to meet your specific market requirements and branding needs."
    },
    {
      title: "Competitive Pricing",
      description: "Direct partnerships with manufacturers and efficient operations allow us to offer the best value without compromising on quality."
    }
  ],
  stats: [
    { number: "1600+", label: "Products" },
    { number: "220+", label: "Brands" },
    { number: "36", label: "Categories" },
    { number: "8+", label: "Years Experience" }
  ]
};

const About = () => {
  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 section-teal text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">About Triton Global Trade</h1>
          <div className="decorative-line max-w-md"></div>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto mt-6 text-gray-100">
            {companyData.tagline}
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {companyData.stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold" style={{ color: 'var(--gold-accent)' }}>
                  {stat.number}
                </div>
                <div className="text-sm sm:text-base mt-2" style={{ color: 'var(--text-medium)' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: 'var(--primary-teal)' }}>
                Who We Are
              </h2>
              <div className="decorative-line max-w-xs mb-6"></div>
              <p className="text-base sm:text-lg mb-4" style={{ color: 'var(--text-medium)' }}>
                {companyData.description}
              </p>
              <p className="text-sm sm:text-base mb-6" style={{ color: 'var(--text-medium)' }}>
                Founded in 2025 and based in Navi Mumbai, India, Triton Global Trade is led by a founder with over 8 years of deep expertise in the export industry. Our team combines extensive market knowledge with a commitment to excellence, ensuring that every shipment meets the expectations of our international partners.
              </p>
              <p className="text-sm sm:text-base" style={{ color: 'var(--text-medium)' }}>
                We understand the unique requirements of international markets and work closely with our clients to provide tailored solutions. Whether you need specific packaging formats, private labeling, or mixed container shipments, we have the expertise and flexibility to deliver.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://images.pexels.com/photos/31280796/pexels-photo-31280796.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Premium Indian Spices"
                className="rounded-lg shadow-lg"
              />
              <img 
                src="https://images.unsplash.com/photo-1644079446600-219068676743?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzcxNTkyNDQ4fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern Warehouse Operations"
                className="rounded-lg shadow-lg mt-8"
              />
              <img 
                src="https://images.unsplash.com/photo-1678182451047-196f22a4143e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxzaGlwcGluZyUyMGNvbnRhaW5lcnN8ZW58MHx8fHwxNzcxNTkyNDAzfDA&ixlib=rb-4.1.0&q=85"
                alt="Global Shipping Network"
                className="rounded-lg shadow-lg"
              />
              <img 
                src="https://images.pexels.com/photos/33175650/pexels-photo-33175650.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Business Partnership"
                className="rounded-lg shadow-lg mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 section-cream pattern-overlay">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="card-hover border-0 shadow-xl">
              <CardHeader>
                <div className="w-16 h-16 rounded-full bg-[var(--primary-teal)] flex items-center justify-center mb-4">
                  <Target size={32} className="text-white" />
                </div>
                <CardTitle className="text-2xl sm:text-3xl" style={{ color: 'var(--primary-teal)' }}>
                  Our Mission
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base sm:text-lg" style={{ color: 'var(--text-medium)' }}>
                  {companyData.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover border-0 shadow-xl">
              <CardHeader>
                <div className="w-16 h-16 rounded-full bg-[var(--gold-accent)] flex items-center justify-center mb-4">
                  <Globe size={32} className="text-white" />
                </div>
                <CardTitle className="text-2xl sm:text-3xl" style={{ color: 'var(--primary-teal)' }}>
                  Our Vision
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base sm:text-lg" style={{ color: 'var(--text-medium)' }}>
                  {companyData.vision}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: 'var(--primary-teal)' }}>
              Our Core Values
            </h2>
            <div className="decorative-line max-w-md"></div>
            <p className="text-base sm:text-lg mt-4" style={{ color: 'var(--text-medium)' }}>
              The principles that guide every decision we make
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyData.values.map((value, idx) => {
              const icons = [Award, Handshake, Heart, TrendingUp];
              const Icon = icons[idx];
              return (
                <Card key={idx} className="card-hover text-center border-0 shadow-lg">
                  <CardContent className="p-6 sm:p-8">
                    <div 
                      className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'var(--gold-accent)' }}
                    >
                      <Icon size={32} className="text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold mb-2" style={{ color: 'var(--primary-teal)' }}>
                      {value.title}
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--text-medium)' }}>
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 section-cream">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: 'var(--primary-teal)' }}>
              Why Partner With Us?
            </h2>
            <div className="decorative-line max-w-md"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {companyData.whyChooseUs.map((item, idx) => {
              const icons = [Globe, Award, Building2, TrendingUp];
              const Icon = icons[idx];
              return (
                <Card key={idx} className="card-hover border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--primary-teal)' }}
                    >
                      <Icon size={24} className="text-white" />
                    </div>
                    <CardTitle className="text-lg sm:text-xl" style={{ color: 'var(--primary-teal)' }}>
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pl-20">
                    <p className="text-sm sm:text-base" style={{ color: 'var(--text-medium)' }}>
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="rounded-2xl p-8 sm:p-12 text-center text-white"
            style={{
              background: 'linear-gradient(135deg, var(--primary-teal) 0%, var(--primary-teal-dark) 100%)'
            }}
          >
            <h2 className="text-2xl sm:text-4xl font-bold mb-4">
              Let's Build Trust Together
            </h2>
            <p className="text-base sm:text-xl mb-8 text-gray-100">
              Experience the Triton difference in global trade
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products">
                <Button className="btn-gold text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6" data-testid="view-products-btn">
                  View Our Products
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 bg-white/10 backdrop-blur-sm text-white border-white hover:bg-white hover:text-[var(--primary-teal)]" data-testid="contact-us-btn">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
